import axios from './axiosConfig'
import type { PosCatalogPayload, PendingOfflineOrder } from 'store/posStore'

export interface PullPosDataResponse {
 status: string
 message?: string
 data: PosCatalogPayload
}

export interface PullPosDataParams {
 products_page?: number
 products_per_page?: number
 customers_limit?: number
}

export interface PushOfflineOrderResult {
 client_uuid: string
 status: 'created' | 'duplicate' | 'error' | 'conflict'
 sale_id?: number
 invoice_number?: string
 print_sequence?: number | null
 message?: string
 code?: string
 recoverable?: boolean
 allowed_actions?: string[]
 field?: string
 correlation_id?: string
}

export interface PushOfflineOrdersResponse {
 status: string
 message?: string
 data: PushOfflineOrderResult[]
}

export interface RetryOfflineOrderResponse {
 status: string
 message?: string
 data?: {
  client_uuid: string
  sale_id?: number
  invoice_number?: string
  print_sequence?: number | null
 }
}

export interface DeviceHeartbeatPayload {
 device_id: string
 device_name?: string
 branch_id: string
 app_version?: string
 shift_id?: string
 pending_client_uuids: string[]
 failed_client_uuids: string[]
}

export interface DismissFailedSyncAuditPayload {
 client_uuids: string[]
 action: string
 reason?: string
}

export const syncAPI = {
 pullPosData: async (branchId?: string | null, params?: PullPosDataParams) => {
 const response = await axios.get<PullPosDataResponse>('/sync/pos-data', {
 params: {
 ...(branchId ? { branch_id: branchId } : {}),
 ...(params ?? {}),
 },
 })
 return response.data
 },

 clientCompatibility: async (clientVersion?: string) => {
 const response = await axios.get('/system/client-compatibility', {
 params: clientVersion ? { client_version: clientVersion } : undefined,
 headers: clientVersion ? { 'X-Client-Version': clientVersion } : undefined,
 })
 return response.data
 },

 pullFullPosData: async (
 branchId?: string | null,
 options: { productsPerPage?: number; customersLimit?: number } = {},
 ) => {
 const productsPerPage = options.productsPerPage ?? 300
 const customersLimit = options.customersLimit ?? 500
 const first = await syncAPI.pullPosData(branchId, {
 products_page: 1,
 products_per_page: productsPerPage,
 customers_limit: customersLimit,
 })

 const productPages = first.data?.pagination?.products
 if (!productPages || productPages.last_page <= 1) return first

 const productsById = new Map<number, PosCatalogPayload['products'][number]>()
 ;(first.data.products ?? []).forEach((product) => productsById.set(Number(product.id), product))

 for (let page = 2; page <= productPages.last_page; page += 1) {
 const next = await syncAPI.pullPosData(branchId, {
 products_page: page,
 products_per_page: productsPerPage,
 customers_limit: customersLimit,
 })
 ;(next.data?.products ?? []).forEach((product) => {
 productsById.set(Number(product.id), product)
 })
 }

 return {
 ...first,
 data: {
 ...first.data,
 products: Array.from(productsById.values()),
 pagination: {
 ...first.data.pagination,
 products: {
 ...productPages,
 current_page: productPages.last_page,
 },
 },
 },
 }
 },

 pushOfflineOrders: async (orders: PendingOfflineOrder[], branchId?: string | null) => {
 const branchConfig = branchId
 ? {
 params: { branch_id: branchId },
 headers: { 'X-Branch-Id': branchId },
 }
 : undefined
 const response = await axios.post<PushOfflineOrdersResponse>(
 '/sync/offline-orders',
 { orders },
 branchConfig,
 )
 return response.data
 },

 retryOfflineOrder: async (clientUuid: string, branchId?: string | null) => {
 const branchConfig = branchId
 ? {
 headers: { 'X-Branch-Id': branchId },
 }
 : undefined
 const response = await axios.post<RetryOfflineOrderResponse>(
 `/sync/offline-orders/${clientUuid}/retry`,
 undefined,
 branchConfig,
 )
 return response.data
 },

 replaceOfflinePaymentAccount: async (
 clientUuid: string,
 body: {
 client_line_id: string
 replacement_financial_account_id: string
 reason: string
 },
 branchId?: string | null,
 ) => {
 const response = await axios.post(`/sync/offline-orders/${clientUuid}/replace-account`, body, {
 headers: branchId ? { 'X-Branch-Id': branchId } : undefined,
 })
 return response.data
 },

 cancelOfflineOrder: async (clientUuid: string, reason: string, branchId?: string | null) => {
 const response = await axios.post(
 `/sync/offline-orders/${clientUuid}/cancel`,
 { reason },
 { headers: branchId ? { 'X-Branch-Id': branchId } : undefined },
 )
 return response.data
 },

 postDeviceHeartbeat: async (payload: DeviceHeartbeatPayload) => {
 const response = await axios.post<{ status: string; message?: string }>(
 '/sync/device-heartbeat',
 payload,
 {
 headers: { 'X-Branch-Id': payload.branch_id },
 },
 )
 return response.data
 },

 logDismissedFailedSync: async (payload: DismissFailedSyncAuditPayload, branchId?: string | null) => {
 const response = await axios.post<{ status: string; message?: string }>(
 '/sync/offline-orders/dismiss-audit',
 payload,
 branchId ? { headers: { 'X-Branch-Id': branchId } } : undefined,
 )
 return response.data
 },
}
